//import { CommonModule, DOCUMENT } from '@angular/common';
//import { Component, Inject, OnInit } from '@angular/core';

import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ApiCallService } from '../../Services/ApiCallService';


interface WhyChooseCard {
  id: number;
  title: string;
  description: string;
  imagePath: string;
  displayOrder: number;
}

export interface TreatmentItemModel {
  TreatmentItemID: number;
  TreatmentName: string;
  MainCategory: string | null;
  Description: string | null;
  Benefits: string | null;
  Price: number;
  CityID: number | null;
  Image: string | null;
  IsActive?: number | boolean;
}

interface IVTherapyFAQ {
  FAQID: number;
  QuestionNo: number;
  Question: string;
  Answer: string;
  IsActive: boolean;
}

interface FAQApiResponse {
  Success: boolean;
  Data: IVTherapyFAQ[];
}

interface BenefitSection {
  mainTitle: string;
  mainDescription: string;
  subTitle: string;
  imagePath: string;
  benefits: IVTherapyBenefit[];
}

// @Component({
//   selector: 'app-houseofhydration',
//   standalone: true,
//   imports: [CommonModule],
//   templateUrl: './houseofhydration.html',
//   styleUrl: './houseofhydration.css',
// })

@Component({
  selector: 'app-houseofhydration',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './houseofhydration.html',
  styleUrl: './houseofhydration.css',
  host: { ngSkipHydration: 'true' },
})

export class Houseofhydration implements OnInit {

  // API URL  
  private apiUrl =
    'https://crm-api.redmonkwellness.com/api/IVTherapyBenefits/GetAll';

  // ==========================================
  // IV THERAPY DATA
  // ==========================================

  ivTherapyBenefits: IVTherapyBenefit[] = [];
  cards: WhyChooseCard[] = [];
  faqs: IVTherapyFAQ[] = [];

  sections: BenefitSection[] = [];

  loading = true;

  mainTitle: string = '';
  mainDescription: string = '';
  subTitle: string = '';
  imagePath: string = '';

  constructor(
    private title: Title,
    private meta: Meta,
    private http: HttpClient,
    private api: ApiCallService,
    @Inject(DOCUMENT) private document: Document,
  @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    this.setSeo();
    this.loadItems();                               // still runs on the server for SEO
  if (!isPlatformBrowser(this.platformId)) return; // API calls only in the browser

    // IV Therapy Benefits
    this.getIVTherapyBenefits();

    // Why Choose Cards
    this.getWhyChooseCards();

    // FAQ
    this.getFAQs();
  }


  baseImageUrl = 'https://crm-api.redmonkwellness.com/';

  loadingItems = false;
  failedImages = new Set<string>();
  wellnessItems: TreatmentItemModel[] = [];
  private readonly WELLNESS_CATEGORY = 'Wellness I.V. Therapy';

  // ---------- Wellness I.V. items (Calendar/GetAllTreatmentItems) ----------
  loadItems(): void {
    if (this.loadingItems) return;
    this.loadingItems = true;

    this.api.postData('Calendar', 'GetAllTreatmentItems', {}).subscribe({
      next: (res: any) => {
        this.loadingItems = false;
        if (!res?.Success) return;

        const items = (res.TreatmentItems || []) as TreatmentItemModel[];
        this.wellnessItems = this.pickByCategory(items, this.WELLNESS_CATEGORY);
      },
      error: (err) => {
        this.loadingItems = false;
        console.error(err);
      },
    });
  }

  private pickByCategory(items: TreatmentItemModel[], category: string): TreatmentItemModel[] {
    const seen = new Set<string>();
    return items
      .filter((i) => (i.MainCategory || '').trim().toLowerCase() === category.toLowerCase() &&
        Number(i.IsActive ?? 0) === 0)
      .sort((a, b) => a.TreatmentItemID - b.TreatmentItemID)
      .filter((i) => {
        const key = (i.TreatmentName || '').trim().toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  // ---------- Image helpers ----------
  imgUrl(path: string | null | undefined, base: string = this.baseImageUrl): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path) || path.startsWith('data:')) return path;
    return base + path.replace(/^\/+/, '');
  }

  onImgError(key: string): void {
    this.failedImages.add(key);
  }

  // ==========================================
  // GET IV THERAPY BENEFITS
  // ==========================================


  // imagePath: string = '';

  //private apiBaseUrl = 'https://localhost:44394';
  private apiBaseUrl = 'https://crm-api.redmonkwellness.com';

  getImageUrl(imagePath: string): string {

    // if (!imagePath) {
    //   return '/assets/images/hoh/benefits.jpg';
    // }

    // If API returns Base64 image
    if (imagePath.startsWith('data:image')) {
      return imagePath;
    }

    // If API returns complete URL
    if (imagePath.startsWith('http://') ||
      imagePath.startsWith('https://')) {
      return imagePath;
    }

    // If API returns server path
    return this.apiBaseUrl + imagePath;
  }

  getIVTherapyBenefits(): void {

    this.http.get<IVTherapyBenefit[]>(this.apiUrl).subscribe({

      next: (response) => {

        if (!response || response.length === 0) {
          this.ivTherapyBenefits = [];
          this.sections = [];
          return;
        }

        // active, not deleted, ordered by DisplayOrder
        this.ivTherapyBenefits = response
          .filter(item => item.IsActive && !item.IsDelete)
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

        // one section per main title
        this.sections = this.buildSections(this.ivTherapyBenefits);
      },

      error: (error) => {
        console.error('Error loading IV Therapy Benefits:', error);
        this.ivTherapyBenefits = [];
        this.sections = [];
      }

    });
  }


  // One section per main title, in the order the titles first appear
  private buildSections(list: IVTherapyBenefit[]): BenefitSection[] {
    const map = new Map<string, BenefitSection>();
    const untitled: IVTherapyBenefit[] = [];

    for (const item of list) {
      const title = (item.MainTitle || '').trim();
      if (!title) { untitled.push(item); continue; }

      const key = title.toLowerCase();
      let s = map.get(key);
      if (!s) {
        s = { mainTitle: title, mainDescription: '', subTitle: '', imagePath: '', benefits: [] };
        map.set(key, s);
      }
      // the section takes the first non-empty value found in its rows
      if (!s.mainDescription && item.MainDescription) s.mainDescription = item.MainDescription;
      if (!s.subTitle && item.SubTitle) s.subTitle = item.SubTitle;
      if (!s.imagePath && item.ImagePath) s.imagePath = item.ImagePath;
      s.benefits.push(item);
    }

    const sections = Array.from(map.values());

    // older rows without a main title stay with the first section
    if (untitled.length) {
      if (sections.length) {
        sections[0].benefits.push(...untitled);
      } else {
        const f = untitled[0];
        sections.push({
          mainTitle: '', mainDescription: f.MainDescription || '', subTitle: f.SubTitle || '',
          imagePath: f.ImagePath || '', benefits: untitled
        });
      }
    }
    return sections;
  }

  getWhyChooseCards(): void {
    this.http
      .get<any[]>(
        'https://crm-api.redmonkwellness.com/api/IVTherapyBenefits/GetAllWhyChooseCards'
      )
      .subscribe({
        next: (rows) => {
          this.cards = (rows || [])
            .map((r) => ({
              id: r.ID ?? r.id,
              title: r.Title ?? r.title ?? '',
              description: r.Description ?? r.description ?? '',
              imagePath: r.ImagePath ?? r.imagePath ?? '',
              displayOrder: r.DisplayOrder ?? r.displayOrder ?? 0,
            }))
            .sort((a, b) => a.displayOrder - b.displayOrder);
        },

        error: (e) => {
          console.error('Why choose cards failed', e);
        },
      });
  }


  // ==========================================
  // GET FAQ
  // ==========================================

  getFAQs(): void {

    this.http
      .get<FAQApiResponse>(
        'https://crm-api.redmonkwellness.com/api/IVTherapyBenefits/GetFAQ'
      )
      .subscribe({

        next: (response) => {

          console.log('FAQ API Response:', response);

          if (!response || !response.Success || !response.Data) {
            this.faqs = [];
            return;
          }

          this.faqs = response.Data
            .filter(faq => faq.IsActive)
            .sort(
              (a, b) =>
                (a.QuestionNo || 0) -
                (b.QuestionNo || 0)
            );
        },

        error: (error) => {

          console.error(
            'Error loading FAQs:',
            error
          );

          this.faqs = [];
        }

      })
  }

  // getWhyChooseCards(): void {
  //   this.http
  //     .get<any[]>('https://crm-api.redmonkwellness.com/api/IVTherapyBenefits/GetAllWhyChooseCards')
  //     .subscribe({
  //       next: (rows) => {
  //         this.cards = (rows || [])
  //           .map((r) => ({
  //             id: r.ID ?? r.id,
  //             title: r.Title ?? r.title ?? '',
  //             description: r.Description ?? r.description ?? '',
  //             imagePath: r.ImagePath ?? r.imagePath ?? '',
  //             displayOrder: r.DisplayOrder ?? r.displayOrder ?? 0,
  //           }))
  //           .sort((a, b) => a.displayOrder - b.displayOrder);
  //       },
  //       error: (e) => console.error('Why choose cards failed', e),
  //     });
  // }

  trackByCard(index: number, card: any) {
    return card.id ?? index;
  }


private setSeo(): void {

  const pageTitle =
    'IV Therapy in Mumbai | Wellness IV Drips | Red Monk Wellness';

  const description =
    'Explore wellness IV therapy in Mumbai at Red Monk Wellness, with customized IV drips for hydration, energy, recovery, skin, hair and overall wellness.';

  const keywords =
    'IV therapy Mumbai, wellness IV drips Mumbai, IV hydration Mumbai, vitamin IV therapy Mumbai, Red Monk Wellness, House of Hydration';

  const canonicalUrl =
    'https://www.redmonkwellness.com/houseofhydration';

  const ogImage =
    'https://www.redmonkwellness.com/assets/images/banner/banner4.jpeg';

  this.title.setTitle(pageTitle);

  this.meta.updateTag({
    name: 'description',
    content: description
  });

  this.meta.updateTag({
    name: 'keywords',
    content: keywords
  });

  this.meta.updateTag({
    name: 'robots',
    content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  });

  this.meta.updateTag({
    property: 'og:type',
    content: 'website'
  });

  this.meta.updateTag({
    property: 'og:locale',
    content: 'en_IN'
  });

  this.meta.updateTag({
    property: 'og:site_name',
    content: 'Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:title',
    content: pageTitle
  });

  this.meta.updateTag({
    property: 'og:description',
    content: description
  });

  this.meta.updateTag({
    property: 'og:url',
    content: canonicalUrl
  });

  this.meta.updateTag({
    property: 'og:image',
    content: ogImage
  });

  this.meta.updateTag({
    property: 'og:image:alt',
    content: 'Wellness IV Therapy at Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:image:type',
    content: 'image/jpeg'
  });

  this.meta.updateTag({
    name: 'twitter:card',
    content: 'summary_large_image'
  });

  this.meta.updateTag({
    name: 'twitter:title',
    content: pageTitle
  });

  this.meta.updateTag({
    name: 'twitter:description',
    content: description
  });

  this.meta.updateTag({
    name: 'twitter:image',
    content: ogImage
  });

  this.meta.updateTag({
    name: 'twitter:image:alt',
    content: 'Wellness IV Therapy at Red Monk Wellness'
  });

  let canonical = this.document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    this.document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);

  let sitemap = this.document.querySelector(
    'link[rel="sitemap"]'
  ) as HTMLLinkElement | null;

  if (!sitemap) {
    sitemap = this.document.createElement('link');
    sitemap.setAttribute('rel', 'sitemap');
    sitemap.setAttribute('type', 'application/xml');
    sitemap.setAttribute('title', 'Sitemap');
    this.document.head.appendChild(sitemap);
  }

  sitemap.setAttribute(
    'href',
    'https://www.redmonkwellness.com/sitemap.xml'
  );
}
}

export interface IVTherapyBenefit {
  ID: number;
  MainTitle: string;
  MainDescription: string;
  SubTitle: string;
  BenefitTitle: string;
  BenefitDescription: string;
  ImagePath: string;
  DisplayOrder: number;
  IsActive: boolean;
  IsDelete: boolean;
  CreationDate?: string;
  UpdationDate?: string;
  Success?: boolean;
  ErrorCode?: number;
  ErrorMessage?: string;
}